import React, { useState } from "react";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles";
import { usePatchMutation, useActionMutation } from "../hooks";
import MemoCard from "../components/MemoCard";
import { netDelta, primaryOwner, driverCause, fmtSignedMoney, fmtPct } from "../format";

// generatedMemo — templated draft sentences per section from the decomposition.
function generatedMemo(version, ownership) {
  const drivers = version?.drivers || [];
  const net = netDelta(drivers);
  const top = [...drivers].sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost))[0];
  const owner = primaryOwner(ownership);
  const rr = version?.impact?.monthlyRunRateDelta;
  return {
    what: top ? `Spend changed ${fmtSignedMoney(net)}. The change was primarily driven by ${top.label || top.key}.` : `Spend changed ${fmtSignedMoney(net)}.`,
    why: top ? `${top.label || top.key} accounted for ${fmtSignedMoney(top.deltaCost)} (${fmtPct(top.sharePct)}). The change was ${driverCause(top).toLowerCase()}.` : "No dominant driver.",
    who: owner ? `${owner.label} owns approximately ${fmtSignedMoney(owner.deltaCost)} of the change.${rr != null ? ` The revised run rate adds ${fmtSignedMoney(rr)} per month.` : ""}` : "Ownership not yet resolved.",
    recommend: "Confirm whether the change was approved and expected. Assign the recurring cost to the responsible budget and establish a monthly usage threshold.",
  };
}

const FIELDS = [["what", "Conclusion"], ["why", "Root cause"], ["who", "Ownership and consequence"], ["recommend", "Recommendation"]];

export default function ResolutionTab({ id, summary, version, ownership = [], closed }) {
  const gen = generatedMemo(version, ownership);
  const [form, setForm] = useState(() => ({ what: summary?.what || "", why: summary?.why || "", who: summary?.who || "", recommend: summary?.recommend || "" }));
  const [moreOpen, setMoreOpen] = useState(false);
  const patch = usePatchMutation(id);
  const action = useActionMutation(id);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = (extra) => patch.mutate({ summary: form, ...extra });

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {FIELDS.map(([k, label]) => (
          <MemoCard key={k} title={label} value={form[k]} generated={gen[k]} onChange={set(k)} disabled={closed} />
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button className={primaryButtonCls} disabled={closed || patch.isLoading} onClick={() => save({ status: "closed" })}>Complete investigation</button>
        <button className={secondaryButtonCls} disabled={closed} onClick={() => save()}>Save draft</button>
        <button className={secondaryButtonCls} disabled={closed} onClick={() => save({ status: "needs_review" })}>Request review</button>
        <div className="relative">
          <button type="button" className={secondaryButtonCls} onClick={() => setMoreOpen((o) => !o)}>More ⋯</button>
          {moreOpen && (
            <div className="absolute right-0 z-20 mt-1 w-52 rounded-card border border-card-bd bg-card py-1 shadow-sm" style={{ width: 208 }}>
              {[["report_attach", "Attach to report"], ["followup", "Create follow-up"], ["enforcement_handoff", "Enforcement hand-off"]].map(([kind, label]) => (
                <button key={kind} type="button" className="block w-full px-3 py-1.5 text-left text-[13px] text-text-2 hover:bg-surface-2" onClick={() => { action.mutate({ kind, targetRef: "" }); setMoreOpen(false); }}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      {closed && <div className="text-[12px] text-text-3">This investigation is closed (frozen). Reopen to edit.</div>}
    </>
  );
}
